import React from "react";
import { CalendarIcon } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DatePicker as CalendarDatePicker } from "@/components/ui/datepicker";
import { ClearButton } from "@/components/ui/ClearButton";
import { cn } from "@/utils";

type CalendarProps = React.ComponentProps<typeof CalendarDatePicker>;

const defaultDisplayFormatter = (date: Date) => format(date, "dd/MM/yyyy");
const defaultValueFormatter = (date: Date) => format(date, "yyyy-MM-dd");
const defaultValueParser = (value: string) => {
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
};

export type DatePickerProps = {
  value?: Date | string | null;
  onChange?: (date?: Date) => void;
  onValueChange?: (value?: string) => void;
  placeholder?: string;
  allowClear?: boolean;
  displayFormatter?: (date: Date) => string;
  valueFormatter?: (date: Date) => string;
  valueParser?: (value: string) => Date | undefined;
  buttonClassName?: string;
  wrapperClassName?: string;
  invalid?: boolean;
  calendarClassName?: string;
  popoverContentClassName?: string;
  disabled?: boolean;
  closeOnSelect?: boolean;
  clearAriaLabel?: string;
  ariaLabel?: string;
} & Omit<CalendarProps, "selectedDate" | "onDateSelect" | "className">;

export const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  onValueChange,
  placeholder = "DD/MM/YYYY",
  allowClear = true,
  displayFormatter,
  valueFormatter,
  valueParser,
  buttonClassName,
  wrapperClassName,
  invalid = false,
  calendarClassName,
  popoverContentClassName,
  disabled = false,
  closeOnSelect = true,
  clearAriaLabel = "Clear date",
  ariaLabel,
  ...calendarProps
}) => {
  const [open, setOpen] = React.useState(false);

  const parser = React.useMemo(() => valueParser ?? defaultValueParser, [valueParser]);
  const outputFormatter = React.useMemo(() => valueFormatter ?? defaultValueFormatter, [valueFormatter]);
  const labelFormatter = React.useMemo(
    () => displayFormatter ?? defaultDisplayFormatter,
    [displayFormatter]
  );

  const parsedValue = React.useMemo(() => {
    if (value === null || value === undefined) return undefined;
    if (value instanceof Date) return value;
    const parsed = parser(value);
    return parsed && isValid(parsed) ? parsed : undefined;
  }, [parser, value]);

  const buttonLabel = parsedValue ? labelFormatter(parsedValue) : placeholder;
  const buttonAriaLabel =
    ariaLabel ??
    (parsedValue ? `Change date, current selection ${labelFormatter(parsedValue)}` : "Open date picker");

  const shouldShowClear = allowClear && !disabled && !!parsedValue;

  const handleClose = React.useCallback(() => setOpen(false), []);
  const emitChange = React.useCallback(
    (next?: Date) => {
      onChange?.(next);
      onValueChange?.(next ? outputFormatter(next) : undefined);
    },
    [onChange, onValueChange, outputFormatter]
  );

  const handleSelect = React.useCallback(
    (next?: Date) => {
      emitChange(next);
      if (closeOnSelect && next) handleClose();
    },
    [closeOnSelect, emitChange, handleClose]
  );

  const handleClear = React.useCallback(() => {
    emitChange(undefined);
    handleClose();
  }, [emitChange, handleClose]);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (disabled && nextOpen) return;
      setOpen(nextOpen);
    },
    [disabled]
  );

  return (
    <div className={cn("w-full", wrapperClassName)}>
      <div className="relative">
        <Popover open={open} onOpenChange={handleOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className={cn(
                "relative !flex w-full items-center justify-between rounded-md bg-white pl-4 text-left text-sm font-medium shadow-none hover:bg-white focus-visible:ring-0 focus-visible:ring-offset-0",
                "min-h-[36px]",
                !parsedValue ? "text-gray-400" : "text-gray-700",
                invalid &&
                  "border-destructive focus-visible:border-destructive focus-visible:ring-destructive/30",
                shouldShowClear ? "pr-8" : "pr-4",
                buttonClassName
              )}
              aria-invalid={invalid || undefined}
              aria-label={buttonAriaLabel}
            >
              <span className="flex-1 truncate">{buttonLabel}</span>
              <span className="flex items-center gap-2 text-[#9CA3AF] shrink-0">
                <CalendarIcon className="h-4 w-4" />
              </span>
            </Button>
          </PopoverTrigger>

          <PopoverContent className={cn("w-auto p-0", popoverContentClassName)} align="start">
            <CalendarDatePicker
              {...calendarProps}
              className={calendarClassName}
              selectedDate={parsedValue}
              onDateSelect={handleSelect}
            />
          </PopoverContent>
        </Popover>
        {shouldShowClear && (
          <ClearButton
            onClick={handleClear}
            ariaLabel={clearAriaLabel}
            title={clearAriaLabel}
            className="absolute right-3 top-1/2 z-10 -translate-y-1/2"
          />
        )}
      </div>
    </div>
  );
};
